import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  Link,
  Stack,
  Divider,
} from '@nextiva/next-ui-components';
import { Page } from 'containers';
import { declinedRegistrationData } from 'utils/declinedRegistrationData';
import { SmsCampaignInfo } from 'interface/WizardV3';
import { getApprovedSPCarrier } from 'utils/getApprovedSPCarrier';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { WizardProviderV3 } from './CampaignDialog/WizardContextV3/WizardContextV3';
import { useGetSMSCampaignStatus } from './CampaignDialog/WizardContextV3/useGetSMSCampaignStatus';
import { SMS_CAMPAIGN_STATUS } from './CampaignDialog/constants';
import { RegistrationNotStarted } from './RegistrationStatus/RegistrationNotStarted';
import { RegistrationApproved } from './RegistrationStatus/RegistrationApproved';
import { RegistrationPending } from './RegistrationStatus/RegistrationPending/RegistrationPending';
import { RegistrationInProgress } from './RegistrationStatus/RegistrationInProgress';
import { RegistrationRejected } from './RegistrationStatus/RegistrationRejected';
import CampaignDialogV3 from './CampaignDialog/CampaignDialogV3';
import { SPBusinessMessagingForm } from './SPBusinessMessagingForm/SPBusinessMessagingForm';
import { LinkStyled } from './RequestWebsiteCard/RequestWebsiteCard.styled';
import { useGetSMSCampaignInfo } from './CampaignDialog/WizardContextV3/useGetSMSCampaignInfo';
import { useGetPhoneNumberSoleProprietor } from './CampaignDialog/WizardContextV3/useGetPhoneNumberSoleProprietor';
import { ComplianceFeesDialog } from './CampaignDialog/CustomDialog/CustomDialog';
import { RequestWebsiteCard } from './RequestWebsiteCard/RequestWebsiteCard';
import { Subtitle3Typo } from './CampaignDialog/WizardV3.styled';
import Autom8tlyDialog from './CampaignDialog/Autom8tlyDialog';

const dataTestId = 'PlatformAdminUI-Messaging';
const MessagingNew = () => {
  const {
    data: smsCampaignStatus,
    isLoading: isLoadingSmsCampaignStatus,
  } = useGetSMSCampaignStatus();
  const {
    data: smsCampaignInfo,
    isLoading: isLoadingSmsCampaignInfo,
  } = useGetSMSCampaignInfo();
  const { data: phoneNumberSP } = useGetPhoneNumberSoleProprietor();
  const isLoading = isLoadingSmsCampaignStatus || isLoadingSmsCampaignInfo;
  const isNotStarted = smsCampaignStatus === SMS_CAMPAIGN_STATUS.NOT_STARTED;
  const isInProgress = smsCampaignStatus === SMS_CAMPAIGN_STATUS.NOT_SUBMITTED;
  const isAccepted = smsCampaignStatus === SMS_CAMPAIGN_STATUS.ACCEPTED;
  const isPending = smsCampaignStatus === SMS_CAMPAIGN_STATUS.PENDING;
  const { declinedData, isDeclined } = declinedRegistrationData(
    smsCampaignInfo as SmsCampaignInfo,
  );
  const { carrier, isApprovedSPCarrier } = getApprovedSPCarrier(
    smsCampaignInfo as SmsCampaignInfo,
  );
  const showPhoneNumberSP = isApprovedSPCarrier;
  const openCampaignDialog = !!new URLSearchParams(useLocation().search).get(
    'openCampaignDialog',
  );
  const {
    featureState: { autom8tly },
  } = useContext(FeatureContext);
  const [openNextivaWizard, setOpenNextivaWizard] = useState(
    openCampaignDialog,
  );
  const [openAutom8tlyWizard, setOpenAutom8tlyWizard] = useState(false);
  const [openComplianceFees, setOpenComplianceFees] = useState(false);

  const handleToggleComplianceFeesDialog = () => {
    setOpenComplianceFees((prev) => !prev);
  };
  const handleOpenCampaignDialog = () => {
    if (autom8tly) {
      setOpenAutom8tlyWizard(true);
    } else {
      setOpenNextivaWizard(true);
    }
    setOpenComplianceFees(false);
  };

  useEffect(() => {
    const { pendo, location } = window;
    pendo?.location?.setUrl(`${location.href}/messaging`);
    return () => pendo?.location?.useBrowserUrl();
  }, []);

  return (
    <WizardProviderV3>
      <Page withPageHeader={false}>
        <Stack spacing={5} data-testid={`${dataTestId}-Stack`}>
          <Grid item xs={12}>
            <Typography variant="h6" color="#0B1029" padding="16px 0px">
              Messaging
            </Typography>
            <Divider />
          </Grid>
          {isPending && !isLoading && (
            <Grid item xs={12} marginTop="48px">
              <Typography variant="h5" color="#0B1029">
                Business messaging registration
              </Typography>
            </Grid>
          )}

          {!isAccepted && !isPending && !isLoading && (
            <Stack
              direction="row"
              gap={15}
              data-testid={`${dataTestId}-Description`}
              marginBottom={8}
            >
              <Grid container direction="column" spacing={1} xs={9}>
                <Grid container item marginBottom="40px">
                  <Typography variant="h5" color="#0B1029">
                    Business messaging registration
                  </Typography>
                </Grid>
                <Grid container item direction="column" spacing={1}>
                  <Grid item>
                    <Typography
                      variant="body2"
                      color="#0B1029"
                      fontWeight="800"
                    >
                      Why do I have to do this?
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Subtitle3Typo>
                      Due to recent{' '}
                      <Link
                        href="https://www.nextiva.com/blog/what-is-a2p-10dlc.html"
                        rel="noreferrer"
                        target="_blank"
                        data-testid={`${dataTestId}-LearnMore`}
                        sx={{ color: 'info.main', fontWeight: 500 }}
                      >
                        US phone carrier regulations
                      </Link>
                      , businesses are now required to register phone numbers
                      they are using for text messaging.
                    </Subtitle3Typo>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  direction="column"
                  spacing={1}
                  marginTop="8px"
                >
                  <Grid item>
                    <Typography
                      variant="body2"
                      color="#0B1029"
                      fontWeight="800"
                    >
                      What does this do?
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Subtitle3Typo>
                      This requirement is designed to add visibility around who
                      is sending business texts and the types of messages being
                      sent as part of an industry-wide effort to reduce spam and
                      improve deliverability of legitimate text messages.
                      <br />
                    </Subtitle3Typo>
                    <br />
                    <Subtitle3Typo>
                      Visit{' '}
                      <Link
                        href="https://www.nextiva.com/support/articles/sms-faqs.html"
                        rel="noreferrer"
                        target="_blank"
                        data-testid={`${dataTestId}-OurWebsite`}
                        sx={{ color: 'info.main', fontWeight: 500 }}
                      >
                        our website
                      </Link>{' '}
                      to learn more and see FAQs.
                    </Subtitle3Typo>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container xs={3} justifyContent="right">
                <RequestWebsiteCard />
              </Grid>
            </Stack>
          )}
          {!isLoading && (
            <Stack spacing={5} paddingBottom={10}>
              {(isAccepted || isPending) && (
                <Subtitle3Typo>
                  Businesses are required to register their accounts to enable
                  text messaging.{' '}
                  <LinkStyled
                    href="https://www.nextiva.com/support/articles/10dlc-10-digit-long-code-faqs.html"
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${dataTestId}-LinkMore`}
                  >
                    Learn more.
                  </LinkStyled>
                </Subtitle3Typo>
              )}
              {isNotStarted && (
                <RegistrationNotStarted
                  handleOpenComplianceFeesDialog={
                    handleToggleComplianceFeesDialog
                  }
                  showAutom8tly={autom8tly}
                />
              )}
              {!isNotStarted && <Divider />}
              {isAccepted && <RegistrationApproved showPhoneNumberSP />}
              {isAccepted && <Divider />}
              {isAccepted &&
                showPhoneNumberSP &&
                !phoneNumberSP?.data?.phoneNumber && (
                  <SPBusinessMessagingForm carrier={carrier} />
                )}
              {isInProgress && !isLoading && (
                <RegistrationInProgress
                  smsCampaignData={smsCampaignInfo?.data[0]}
                  handleOpenComplianceFeesDialog={
                    handleToggleComplianceFeesDialog
                  }
                />
              )}
              {isPending && !isLoading && (
                <RegistrationPending
                  smsCampaignData={smsCampaignInfo?.data[0]}
                />
              )}

              {isDeclined && !isLoading && (
                <RegistrationRejected
                  declinedData={declinedData}
                  smsCampaignData={smsCampaignInfo?.data[0]}
                  handleOpenComplianceFeesDialog={
                    handleToggleComplianceFeesDialog
                  }
                />
              )}
              <ComplianceFeesDialog
                handleAccept={handleOpenCampaignDialog}
                handleCancel={handleToggleComplianceFeesDialog}
                openDialog={openComplianceFees}
              />
              {!autom8tly && (
                <CampaignDialogV3
                  open={openNextivaWizard}
                  handleCloseDialog={() => setOpenNextivaWizard(false)}
                />
              )}
              {autom8tly && (
                <Autom8tlyDialog
                  open={openAutom8tlyWizard}
                  handleCloseDialog={() => setOpenAutom8tlyWizard(false)}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Page>
    </WizardProviderV3>
  );
};

export default MessagingNew;
